import React, { useLayoutEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import dayjs from "dayjs";
import { get } from "lodash-es";
import {
  DocumentAttachmentDetailDto,
  DocumentDetailResponse,
} from "apis/data-contracts";
import { DATE_TIME_FORMAT, BLANK_PLACEHOLDER } from "config/constant";
import {
  CONTRACT_INFO_STEP1_FIELD,
  CONTRACT_INFO_STEP2_FIELD,
} from "config/documentConstant";
import { SignInfo } from "../../common/DocumentDetailSignSection";

type PreviewTag = {
  uuid?: string;
  name?: string;
};

interface AnalysisContinuousPreviewProps {
  documentData: Record<string, unknown>;
  documentClassName: string;
  companyName?: string;
  documentApproveStatus: DocumentDetailResponse["approveStatus"];
  tags?: PreviewTag[];
  createdUserInfo: SignInfo;
  approvedUserInfo?: SignInfo;
  showApproverSign?: boolean;
  attachmentList?: DocumentAttachmentDetailDto[];
  approveComment?: string;
  showReviewSection?: boolean;
}

interface ProfitabilityDataRow {
  category?: string;
  amount?: number | string | null;
  note?: string | null;
}

const FIELD1 = CONTRACT_INFO_STEP1_FIELD;
const FIELD2 = CONTRACT_INFO_STEP2_FIELD;
const CELL_VERTICAL_PADDING = "0.6mm";
const CELL_MIN_HEIGHT = "5.8mm";
const MULTILINE_CELL_MIN_HEIGHT = "16mm";
const SUMMARY_LEFT_WIDTH = "48%";
const SUMMARY_RIGHT_WIDTH = "52%";
const SUMMARY_COLUMN_GAP = "4mm";
const RIGHT_GROUP_LABEL_WIDTH = "13mm";
const RIGHT_GROUP_INNER_GAP = "3mm";
const RIGHT_GROUP_LINE_INSET = "5mm";
const BASE_PAPER_WIDTH = "210mm";

const isBlankValue = (value: unknown) =>
  value === undefined || value === null || String(value).trim() === "";

const formatValue = (value: unknown) =>
  isBlankValue(value) ? BLANK_PLACEHOLDER : String(value);

const formatNumber = (value: unknown) => {
  if (isBlankValue(value)) return BLANK_PLACEHOLDER;

  const normalizedValue = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(normalizedValue)) return String(value);

  return normalizedValue.toLocaleString("ko-KR");
};

const formatLandArea = (documentData: Record<string, unknown>) => {
  const squareMeters = get(
    documentData,
    FIELD1.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
  );
  const pyeong = get(
    documentData,
    FIELD1.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key,
  );

  const squareMetersText = isBlankValue(squareMeters)
    ? ""
    : `${formatNumber(squareMeters)}㎡`;
  const pyeongText = isBlankValue(pyeong) ? "" : `${formatNumber(pyeong)}평`;

  if (squareMetersText && pyeongText)
    return `${squareMetersText} (${pyeongText})`;
  return squareMetersText || pyeongText || BLANK_PLACEHOLDER;
};

const getProfitabilityUnitLabel = (unit?: string) => {
  switch (unit) {
    case "1000":
      return "천원";
    case "1000000":
      return "백만원";
    case "1":
    default:
      return "원";
  }
};

const formatProfitabilityAmount = (value: unknown, unit = "1") => {
  if (isBlankValue(value)) return BLANK_PLACEHOLDER;

  const normalizedValue = Number(String(value).replace(/,/g, ""));
  const unitValue = Number(unit);
  if (
    Number.isNaN(normalizedValue) ||
    Number.isNaN(unitValue) ||
    unitValue <= 0
  ) {
    return String(value);
  }

  const displayValue = normalizedValue / unitValue;
  if (displayValue % 1 === 0) return displayValue.toLocaleString("ko-KR");
  return displayValue.toLocaleString("ko-KR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const getSharedProfitabilityUnit = (units?: Record<string, string>) => {
  return (
    units?.["0"] ||
    Object.values(units || {}).find(
      (value): value is string => typeof value === "string" && value.length > 0,
    ) ||
    "1"
  );
};

const normalizeProfitabilityRows = (rows: ProfitabilityDataRow[]) => {
  return FIELD2.PROFITABILITY.rows.map((defaultRow, index) => ({
    ...defaultRow,
    ...(rows[index] ?? {}),
    category: rows[index]?.category || defaultRow.category,
  }));
};

const OCTOLogo = () => (
  <svg
    width="82"
    height="25"
    viewBox="0 0 185 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 22.4789C0 20.3925 0.281605 18.3847 0.852036 16.4627C1.41525 14.5406 2.2023 12.7472 3.22041 11.0824C4.2313 9.41758 5.4588 7.89567 6.90293 6.5381C8.34706 5.18052 9.92838 4.00872 11.6686 3.03699C13.4087 2.06525 15.2789 1.31501 17.2862 0.786273C19.2936 0.257534 21.3803 -0.00683594 23.5537 -0.00683594C25.7271 -0.00683594 27.7922 0.257534 29.8068 0.786273C31.8214 1.31501 33.6987 2.06525 35.4389 3.03699C37.1791 4.00872 38.7676 5.17338 40.2045 6.5381C41.6487 7.90282 42.8762 9.41758 43.9015 11.0824C44.9268 12.7472 45.7211 14.5478 46.2843 16.4627C46.8475 18.3847 47.1363 20.3925 47.1363 22.4789C47.1363 24.5652 46.8547 26.573 46.2843 28.4951C45.7211 30.4171 44.9268 32.2105 43.9015 33.8825C42.8762 35.5473 41.6487 37.0621 40.2045 38.4268C38.7604 39.7915 37.1791 40.9633 35.4389 41.9422C33.6987 42.9211 31.8214 43.6785 29.8068 44.2072C27.7922 44.7359 25.7055 45.0003 23.5537 45.0003C21.402 45.0003 19.2863 44.7359 17.2862 44.2072C15.2789 43.6785 13.4087 42.9211 11.6686 41.9422C9.92838 40.9633 8.33984 39.7915 6.90293 38.4268C5.4588 37.0692 4.2313 35.5544 3.22041 33.8825C2.20952 32.2105 1.41525 30.4171 0.852036 28.4951C0.281605 26.573 0 24.5652 0 22.4789ZM7.97159 22.5146C7.97159 24.7296 8.38316 26.7945 9.20632 28.7023C10.0295 30.61 11.1414 32.2677 12.5495 33.6753C13.9575 35.0829 15.611 36.1832 17.4956 36.9835C19.3802 37.7837 21.402 38.1839 23.5609 38.1839C25.7199 38.1839 27.7778 37.7837 29.6696 36.9835C31.5686 36.1832 33.2222 35.0757 34.6302 33.6753C36.0382 32.2748 37.1502 30.6172 37.9589 28.7023C38.7676 26.7874 39.172 24.7296 39.172 22.5146C39.172 20.2996 38.7676 18.2275 37.9589 16.3055C37.1502 14.3834 36.0382 12.7258 34.6302 11.3182C33.2222 9.9106 31.5686 8.81025 29.6696 8.00999C27.7706 7.20974 25.7344 6.80247 23.5609 6.80247C21.3875 6.80247 19.3802 7.2026 17.4956 8.00999C15.611 8.81025 13.9575 9.9106 12.5495 11.3182C11.1414 12.7186 10.0295 14.3834 9.20632 16.3055C8.38316 18.2275 7.97159 20.2925 7.97159 22.5146Z"
      fill="#4E2D36"
    />
    <path
      d="M90.9794 15.7693C90.4017 14.3546 89.6363 13.097 88.676 11.9824C87.7156 10.8749 86.6253 9.93172 85.3978 9.1672C84.1703 8.39552 82.8417 7.80962 81.412 7.40949C79.9823 7.00937 78.5238 6.8093 77.0291 6.8093C74.8701 6.8093 72.8556 7.20943 70.9638 8.01683C69.0792 8.81708 67.4329 9.91743 66.0249 11.325C64.6168 12.7255 63.4976 14.3903 62.6817 16.3123C61.8585 18.2344 61.447 20.2993 61.447 22.5214C61.447 24.7436 61.8585 26.8014 62.6817 28.7091C63.5048 30.624 64.6168 32.2745 66.0249 33.6821C67.4329 35.0897 69.0792 36.1901 70.9638 36.9903C72.8556 37.7906 74.8701 38.1907 77.0291 38.1907C78.5238 38.1907 79.9751 37.9978 81.3976 37.6048C82.8128 37.2118 84.1342 36.6402 85.3617 35.8828C86.5892 35.1254 87.6795 34.1894 88.6399 33.0819C89.6002 31.9744 90.3656 30.7097 90.9433 29.295H99.1387C98.4311 31.8244 97.3191 34.068 95.7955 36.0186C94.272 37.9763 92.4957 39.6126 90.4812 40.9487C88.4666 42.2777 86.2932 43.2852 83.9681 43.9711C81.6431 44.657 79.3325 45 77.0219 45C74.8412 45 72.7545 44.7356 70.7472 44.2069C68.7398 43.6782 66.8697 42.9279 65.1295 41.9419C63.3893 40.963 61.8008 39.7912 60.3639 38.4265C58.927 37.0689 57.6995 35.5541 56.6813 33.8822C55.6705 32.2174 54.8762 30.4168 54.313 28.4948C53.7498 26.5727 53.4609 24.5649 53.4609 22.4786C53.4609 20.3922 53.7425 18.3844 54.313 16.4624C54.8762 14.5403 55.6632 12.7469 56.6813 11.0821C57.6922 9.41728 58.9197 7.90251 60.3639 6.53779C61.808 5.18022 63.3893 4.00842 65.1295 3.03668C66.8697 2.06494 68.7398 1.3147 70.7472 0.785964C72.7545 0.26437 74.8485 0 77.0219 0C79.3253 0 81.6431 0.328676 83.9681 0.993172C86.2932 1.65767 88.4666 2.65084 90.4812 3.97269C92.4957 5.29454 94.2648 6.93792 95.7955 8.90997C97.3191 10.882 98.4383 13.1828 99.1387 15.7979L90.9794 15.7693Z"
      fill="#4E2D36"
    />
    <path
      d="M114.756 7.82395H101.824V1.01465H135.682V7.82395H122.721V44.0783H114.756V7.82395Z"
      fill="#4E2D36"
    />
    <path
      d="M137.863 22.4789C137.863 20.3925 138.145 18.3847 138.715 16.4627C139.279 14.5406 140.066 12.7472 141.084 11.0824C142.095 9.41758 143.322 7.89567 144.766 6.5381C146.21 5.18052 147.792 4.00872 149.539 3.03699C151.279 2.06525 153.149 1.31501 155.157 0.786273C157.157 0.257534 159.251 -0.00683594 161.431 -0.00683594C163.612 -0.00683594 165.67 0.257534 167.685 0.786273C169.699 1.31501 171.576 2.06525 173.317 3.03699C175.057 4.00872 176.638 5.17338 178.082 6.5381C179.526 7.90282 180.761 9.41758 181.779 11.0824C182.805 12.7472 183.599 14.5478 184.162 16.4627C184.725 18.3847 185.007 20.3925 185.007 22.4789C185.007 24.5652 184.725 26.573 184.162 28.4951C183.599 30.4171 182.797 32.2105 181.779 33.8825C180.761 35.5473 179.526 37.0621 178.082 38.4268C176.638 39.7915 175.057 40.9633 173.317 41.9422C171.576 42.9211 169.699 43.6785 167.685 44.2072C165.67 44.7359 163.583 45.0003 161.431 45.0003C159.28 45.0003 157.164 44.7359 155.157 44.2072C153.149 43.6785 151.279 42.9211 149.539 41.9422C147.799 40.9633 146.21 39.7915 144.766 38.4268C143.322 37.0692 142.102 35.5544 141.084 33.8825C140.073 32.2105 139.279 30.4171 138.715 28.4951C138.152 26.573 137.863 24.5652 137.863 22.4789ZM145.835 22.5146C145.835 24.7296 146.246 26.7945 147.07 28.7023C147.893 30.61 149.005 32.2677 150.413 33.6753C151.821 35.0829 153.467 36.1832 155.359 36.9835C157.243 37.7837 159.265 38.1839 161.424 38.1839C163.583 38.1839 165.634 37.7837 167.533 36.9835C169.432 36.1832 171.085 35.0757 172.493 33.6753C173.902 32.2748 175.013 30.6172 175.822 28.7023C176.631 26.7874 177.042 24.7296 177.042 22.5146C177.042 20.2996 176.631 18.2275 175.822 16.3055C175.013 14.3834 173.902 12.7258 172.493 11.3182C171.085 9.9106 169.432 8.81025 167.533 8.00999C165.634 7.20974 163.598 6.80247 161.424 6.80247C159.251 6.80247 157.243 7.2026 155.359 8.00999C153.474 8.81025 151.821 9.9106 150.413 11.3182C149.005 12.7186 147.886 14.3834 147.07 16.3055C146.246 18.2275 145.835 20.2925 145.835 22.5146Z"
      fill="#4E2D36"
    />
  </svg>
);

const AnalysisContinuousPreview = ({
  documentData,
  documentClassName,
  companyName,
  documentApproveStatus,
  tags = [],
  createdUserInfo,
  approvedUserInfo,
  showApproverSign,
  attachmentList = [],
  approveComment = "",
  showReviewSection = true,
}: AnalysisContinuousPreviewProps) => {
  const location = formatValue(
    documentData[FIELD2.BASIC_REVIEW.fields.LOCATION.key],
  );
  const businessRightAcquisition = formatValue(
    documentData[FIELD2.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.key],
  );
  const permission = formatValue(
    documentData[FIELD2.BASIC_REVIEW.fields.PERMISSION.key],
  );
  const marketStatus = formatValue(
    documentData[FIELD2.BASIC_REVIEW.fields.MARKET_STATUS.key],
  );
  const appropriateSalePrice = formatValue(
    documentData[FIELD2.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.key],
  );
  const reviewResult = formatValue(documentData[FIELD2.REVIEW_RESULT.key]);
  const processingResult = documentData[FIELD2.PROCESSING_RESULT.key];
  const profitabilityRows =
    (documentData[FIELD2.PROFITABILITY.key] as
      | ProfitabilityDataRow[]
      | undefined) || [];
  const profitabilityUnits =
    (documentData[FIELD2.PROFITABILITY_UNITS.key] as
      | Record<string, string>
      | undefined) || {};
  const normalizedProfitabilityRows =
    normalizeProfitabilityRows(profitabilityRows);
  const sharedProfitabilityUnit =
    getSharedProfitabilityUnit(profitabilityUnits);
  const costRows = normalizedProfitabilityRows.slice(1, 5);
  const profitRows = normalizedProfitabilityRows.slice(5, 7);

  const overviewRows = [
    [
      { label: "PJ명", value: formatValue(documentData[FIELD1.PJ.key]) },
      {
        label: "정보제공자",
        value: formatValue(documentData[FIELD1.INFORMATION_PROVIDER.key]),
      },
    ],
    [
      {
        label: "접수일",
        value: formatValue(documentData[FIELD1.RECEIPT_DATE.key]),
      },
      {
        label: "사업방식",
        value: formatValue(documentData[FIELD1.BUSINESS_METHOD.key]),
      },
    ],
    [
      {
        label: "시행사 및 관계사",
        value: formatValue(
          documentData[FIELD1.IMPLEMENTER_AND_RELATED_COMPANY.key],
        ),
      },
      { label: "토지면적", value: formatLandArea(documentData) },
    ],
  ];

  const optionalOverviewRows = [
    {
      visible:
        !isBlankValue(documentData[FIELD1.URBAN_MANAGEMENT_PLAN.key]) ||
        !isBlankValue(documentData[FIELD1.BUSINESS_SCALE.key]),
      values: [
        {
          label: "도시관리 계획",
          value: formatValue(documentData[FIELD1.URBAN_MANAGEMENT_PLAN.key]),
        },
        {
          label: "사업규모",
          value: formatValue(documentData[FIELD1.BUSINESS_SCALE.key]),
        },
      ],
    },
  ];

  const hasAttachmentOrComment =
    attachmentList.length > 0 || approveComment.trim().length > 0;
  const shortReviewResult =
    typeof documentData[FIELD2.REVIEW_RESULT.key] === "string" &&
    String(documentData[FIELD2.REVIEW_RESULT.key]).trim().length <= 80 &&
    !String(documentData[FIELD2.REVIEW_RESULT.key]).includes("\n");
  const frameRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [previewMetrics, setPreviewMetrics] = useState({
    height: 0,
    scale: 1,
  });

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const paper = paperRef.current;
    if (!frame || !paper) return;

    let animationFrameId = 0;

    const updatePreviewMetrics = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        const paperWidth = paper.offsetWidth || 1;
        const nextScale = Math.max(0.1, frame.clientWidth / paperWidth);
        const nextHeight = paper.offsetHeight;

        setPreviewMetrics((prev) => {
          if (
            Math.abs(prev.scale - nextScale) < 0.001 &&
            Math.abs(prev.height - nextHeight) < 1
          ) {
            return prev;
          }

          return {
            height: nextHeight,
            scale: nextScale,
          };
        });
      });
    };

    updatePreviewMetrics();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updatePreviewMetrics);
      return () => {
        cancelAnimationFrame(animationFrameId);
        window.removeEventListener("resize", updatePreviewMetrics);
      };
    }

    const resizeObserver = new ResizeObserver(updatePreviewMetrics);
    resizeObserver.observe(frame);
    resizeObserver.observe(paper);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <PreviewFrame
      ref={frameRef}
      $height={previewMetrics.height}
      $scale={previewMetrics.scale}
    >
      <Paper ref={paperRef} $scale={previewMetrics.scale}>
        <Header>
          <OCTOLogo />
          <HeaderRight>
            <Title>{documentClassName}</Title>
            {companyName && <CompanyName>{companyName}</CompanyName>}
          </HeaderRight>
        </Header>

        <Section>
          <SectionTitle>1. 사업 개요</SectionTitle>
          <TableBox>
            {overviewRows.map((row, index) => (
              <TableRow key={index}>
                {row.map((field) => (
                  <HalfField key={field.label}>
                    <FieldLabel>{field.label}</FieldLabel>
                    <FieldValue>{field.value}</FieldValue>
                  </HalfField>
                ))}
              </TableRow>
            ))}

            {optionalOverviewRows.map(
              (row) =>
                row.visible && (
                  <TableRow key={row.values[0].label}>
                    {row.values.map((field) => (
                      <HalfField key={field.label}>
                        <FieldLabel>{field.label}</FieldLabel>
                        <FieldValue>{field.value}</FieldValue>
                      </HalfField>
                    ))}
                  </TableRow>
                ),
            )}

            {!isBlankValue(documentData[FIELD1.ADDITIONAL_NOTES.key]) && (
              <TableRow $last>
                <FullField>
                  <FieldLabel>기타사항</FieldLabel>
                  <FieldValue $multiline>
                    {formatValue(documentData[FIELD1.ADDITIONAL_NOTES.key])}
                  </FieldValue>
                </FullField>
              </TableRow>
            )}
          </TableBox>
        </Section>

        {showReviewSection && (
          <Section>
            <SectionTitle>2. 검토 결과</SectionTitle>
            <TableBox>
              {/* 행1: 사업권원확보(좌) | 인허가(우) */}
              <PairRow $minHeight="6mm">
                <HalfField>
                  <FieldLabel>사업권원확보</FieldLabel>
                  <FieldValue $multiline>{businessRightAcquisition}</FieldValue>
                </HalfField>
                <RightField>
                  <FieldLabel>인허가</FieldLabel>
                  <FieldValue $multiline>{permission}</FieldValue>
                </RightField>
              </PairRow>

              {/* 행2: 입지(좌) | 적정분양가(우) */}
              <PairRow $minHeight="19mm">
                <HalfField>
                  <FieldLabel>입지</FieldLabel>
                  <FieldValue $multiline>{location}</FieldValue>
                </HalfField>
                <RightField>
                  <FieldLabel>적정분양가</FieldLabel>
                  <FieldValue $multiline>{appropriateSalePrice}</FieldValue>
                </RightField>
              </PairRow>

              {/* 행3: 시장현황(좌) | 수익성표(우) — 마지막 행, 하단 구분선 없음 */}
              <PairRow $minHeight="29.5mm" $last>
                <HalfField>
                  <FieldLabel>시장현황</FieldLabel>
                  <FieldValue $multiline>{marketStatus}</FieldValue>
                </HalfField>

                <SummaryRight>
                  <ProfitGroup $height="2.5rem">
                    <ProfitGroupLabel>매출액</ProfitGroupLabel>
                    <ProfitGroupContent>
                      <ProfitabilityRow>
                        <ProfitCategory />
                        <ProfitAmount>
                          {formatProfitabilityAmount(
                            normalizedProfitabilityRows[0]?.amount,
                            sharedProfitabilityUnit,
                          )}
                        </ProfitAmount>
                        <ProfitUnit>
                          {getProfitabilityUnitLabel(sharedProfitabilityUnit)}
                        </ProfitUnit>
                        <ProfitNote>
                          {formatValue(normalizedProfitabilityRows[0]?.note)}
                        </ProfitNote>
                      </ProfitabilityRow>
                    </ProfitGroupContent>
                  </ProfitGroup>

                  <ProfitGroup $height="9.0rem">
                    <ProfitGroupLabel>
                      매출
                      <br />
                      원가
                    </ProfitGroupLabel>
                    <ProfitGroupContent>
                      {costRows.map((row, index) => (
                        <ProfitabilityRow
                          key={row?.category || index}
                          $border={index < 3}
                        >
                          <ProfitCategory>
                            {formatValue(row?.category)}
                          </ProfitCategory>
                          <ProfitAmount>
                            {formatProfitabilityAmount(
                              row?.amount,
                              sharedProfitabilityUnit,
                            )}
                          </ProfitAmount>
                          <ProfitUnit>
                            {getProfitabilityUnitLabel(sharedProfitabilityUnit)}
                          </ProfitUnit>
                          <ProfitNote>{formatValue(row?.note)}</ProfitNote>
                        </ProfitabilityRow>
                      ))}
                    </ProfitGroupContent>
                  </ProfitGroup>

                  <ProfitGroup $last $height="5.1rem">
                    <ProfitGroupLabel>
                      사업
                      <br />
                      이익
                    </ProfitGroupLabel>
                    <ProfitGroupContent>
                      {profitRows.map((row, index) => (
                        <ProfitabilityRow
                          key={row?.category || index}
                          $border={index < 1}
                        >
                          <ProfitCategory>
                            {formatValue(row?.category)}
                          </ProfitCategory>
                          <ProfitAmount>
                            {formatProfitabilityAmount(
                              row?.amount,
                              sharedProfitabilityUnit,
                            )}
                          </ProfitAmount>
                          <ProfitUnit>
                            {getProfitabilityUnitLabel(sharedProfitabilityUnit)}
                          </ProfitUnit>
                          <ProfitNote>{formatValue(row?.note)}</ProfitNote>
                        </ProfitabilityRow>
                      ))}
                    </ProfitGroupContent>
                  </ProfitGroup>
                </SummaryRight>
              </PairRow>
            </TableBox>

            <ResultRow>
              <FieldLabel>검토 결과</FieldLabel>
              <ResultValue $center={shortReviewResult}>
                {reviewResult}
              </ResultValue>
            </ResultRow>

            <ThinDivider />

            <ResultRow>
              <FieldLabel>처리결과</FieldLabel>
              <ProcessingList>
                {FIELD2.PROCESSING_RESULT.options.map((option) => (
                  <ProcessingItem key={option}>
                    <CheckBox $checked={option === processingResult} />
                    <span>{option}</span>
                  </ProcessingItem>
                ))}
              </ProcessingList>
            </ResultRow>
          </Section>
        )}

        <Footer>
          <FooterTopLine />
          <TagRow>
            <TagLabel>태그</TagLabel>
            <TagList>
              {tags.map((tag, index) => (
                <TagChip key={tag.uuid || `${tag.name}-${index}`}>
                  {tag.name}
                </TagChip>
              ))}
            </TagList>
          </TagRow>

          <SignRow>
            <SignColumn>
              <SignTitle>작성</SignTitle>
              <SignInfoWrap>
                <div>{createdUserInfo.name}</div>
                <div>
                  {createdUserInfo.date
                    ? dayjs(createdUserInfo.date).format(DATE_TIME_FORMAT)
                    : BLANK_PLACEHOLDER}
                </div>
              </SignInfoWrap>
            </SignColumn>
            <SignImageWrap>
              {createdUserInfo.signImageUrl ? (
                <SignImage
                  src={`${createdUserInfo.signImageUrl}?timestamp=${Date.now()}`}
                  alt=""
                />
              ) : (
                <SignPlaceholder>(서명)</SignPlaceholder>
              )}
            </SignImageWrap>
            <SignDivider />
            <SignColumn>
              <SignTitle>승인</SignTitle>
              <SignInfoWrap>
                <div>
                  {showApproverSign || documentApproveStatus === "APPROVE"
                    ? approvedUserInfo?.name || " "
                    : " "}
                </div>
                <div>
                  {showApproverSign || documentApproveStatus === "APPROVE"
                    ? approvedUserInfo?.date
                      ? dayjs(approvedUserInfo.date).format(DATE_TIME_FORMAT)
                      : " "
                    : " "}
                </div>
              </SignInfoWrap>
            </SignColumn>
            <SignImageWrap>
              {showApproverSign || documentApproveStatus === "APPROVE" ? (
                approvedUserInfo?.signImageUrl ? (
                  <SignImage
                    src={`${approvedUserInfo.signImageUrl}?timestamp=${Date.now()}`}
                    alt=""
                  />
                ) : (
                  <SignPlaceholder>(서명)</SignPlaceholder>
                )
              ) : (
                <SignPlaceholder>(서명)</SignPlaceholder>
              )}
            </SignImageWrap>
          </SignRow>

          {hasAttachmentOrComment && (
            <AttachmentCommentRow>
              <AttachmentColumn>
                {attachmentList.length > 0 && (
                  <>
                    <FooterLabel>첨부파일</FooterLabel>
                    <AttachmentList>
                      {attachmentList.map((attachment) => (
                        <li key={attachment.uuid}>{attachment.fileName}</li>
                      ))}
                    </AttachmentList>
                  </>
                )}
              </AttachmentColumn>
              <CommentColumn>
                {approveComment.trim() && (
                  <>
                    <FooterLabel>결재자 코멘트</FooterLabel>
                    <CommentText>{approveComment}</CommentText>
                  </>
                )}
              </CommentColumn>
            </AttachmentCommentRow>
          )}
        </Footer>
      </Paper>
    </PreviewFrame>
  );
};

export default AnalysisContinuousPreview;

const PreviewFrame = styled.div<{ $height: number; $scale: number }>`
  width: 100%;
  height: ${({ $height, $scale }) =>
    $height > 0 ? `${$height * $scale}px` : "auto"};
  margin: 0;
  flex-shrink: 0;
  overflow: hidden;
`;

const Paper = styled.div<{ $scale: number }>`
  width: ${BASE_PAPER_WIDTH};
  max-width: none;
  background: #fff;
  padding: 16mm 10mm 14mm;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 11mm;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: top left;

  ${({ theme }) => theme.media.small} {
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8mm;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.2mm;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 13pt;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.03em;
`;

const CompanyName = styled.div`
  font-size: 6.5pt;
  font-weight: 500;
  line-height: 1.4;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 2.4mm;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 10pt;
  font-weight: 600;
  line-height: 1.66;
  color: #3a4552;
`;

const TableBox = styled.div`
  border-top: 0.7mm solid #4e2d36;
  border-bottom: 0.7mm solid #4e2d36;
`;

const TableRow = styled.div<{ $last?: boolean }>`
  display: flex;
  gap: 6mm;
  min-height: ${CELL_MIN_HEIGHT};
  padding: ${CELL_VERTICAL_PADDING} 0;
  border-bottom: ${({ $last }) => ($last ? "none" : "0.2mm solid #c6c8cd")};
`;

const HalfField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4mm;
`;

const FullField = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4mm;
`;

const FieldLabel = styled.div`
  width: 23.5mm;
  flex-shrink: 0;
  font-size: 9.46pt;
  font-weight: 700;
  line-height: 1.5;
  color: #171d23;
  display: flex;
  align-items: center;
`;

const FieldValue = styled.div<{ $multiline?: boolean }>`
  flex: 1;
  font-size: 9.46pt;
  font-weight: 400;
  line-height: 1.5;
  color: #171d23;
  white-space: ${({ $multiline }) => ($multiline ? "pre-wrap" : "normal")};
  display: flex;
  align-items: center;
`;

const RightField = styled(HalfField)`
  ${FieldLabel} {
    width: 17mm;
  }
`;

const PairRow = styled.div<{ $minHeight?: string; $last?: boolean }>`
  display: grid;
  grid-template-columns: ${SUMMARY_LEFT_WIDTH} ${SUMMARY_RIGHT_WIDTH};
  column-gap: ${SUMMARY_COLUMN_GAP};
  min-height: ${({ $minHeight }) => $minHeight || CELL_MIN_HEIGHT};
  padding: ${CELL_VERTICAL_PADDING} 0;
  border-bottom: ${({ $last }) => ($last ? "none" : "0.2mm solid #c6c8cd")};

  & > * {
    min-width: 0;
  }
`;

const SummaryRight = styled.div`
  min-width: 0;
`;

const ProfitGroup = styled.div<{ $last?: boolean; $height?: string }>`
  position: relative;
  display: grid;
  grid-template-columns: ${RIGHT_GROUP_LABEL_WIDTH} minmax(0, 1fr);
  column-gap: ${RIGHT_GROUP_INNER_GAP};
  padding: 0.8mm 0;
  min-height: ${({ $height }) => $height || "auto"};
  min-width: 0;

  ${({ $last }) =>
    !$last &&
    css`
      &::after {
        content: "";
        position: absolute;
        left: calc(${RIGHT_GROUP_LABEL_WIDTH} + ${RIGHT_GROUP_INNER_GAP});
        right: ${RIGHT_GROUP_LINE_INSET};
        bottom: 0;
        height: 0.2mm;
        background: #c6c8cd;
      }
    `}
`;

const ProfitGroupLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  font-size: 9.46pt;
  font-weight: 700;
  line-height: 1.3;
`;

const ProfitGroupContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const ProfitabilityRow = styled.div<{ $border?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${CELL_MIN_HEIGHT};
  padding: 0;
  box-sizing: border-box;

  ${({ $border }) =>
    $border &&
    css`
      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: ${RIGHT_GROUP_LINE_INSET};
        bottom: 0;
        height: 0.2mm;
        background: #c6c8cd;
      }
    `}
`;

const ProfitCategory = styled.div`
  width: 15mm;
  flex-shrink: 0;
  font-size: 9.46pt;
  font-weight: 600;
`;

const ProfitAmount = styled.div`
  width: 24mm;
  flex-shrink: 0;
  text-align: right;
  font-size: 9.46pt;
`;

const ProfitUnit = styled.div`
  width: 9mm;
  flex-shrink: 0;
  text-align: center;
  font-size: 9.46pt;
`;

const ProfitNote = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 1mm;
  font-size: 9.46pt;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultRow = styled.div`
  display: flex;
  gap: 4mm;
  min-height: ${MULTILINE_CELL_MIN_HEIGHT};
  padding: ${CELL_VERTICAL_PADDING} 0;
  align-items: stretch;
`;

const ResultValue = styled.div<{ $center?: boolean }>`
  flex: 1;
  font-size: 9.46pt;
  line-height: 1.5;
  white-space: pre-wrap;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
`;

const ThinDivider = styled.div`
  height: 0.2mm;
  background: #c6c8cd;
`;

const ProcessingList = styled.div`
  display: flex;
  align-items: center;
  gap: 14.19px;
`;

const ProcessingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5.91px;
  font-size: 9.46pt;
`;

const CheckBox = styled.div<{ $checked: boolean }>`
  width: 14.19px;
  height: 14.19px;
  border-radius: 0.3rem;
  border: 0.1rem solid ${({ $checked }) => ($checked ? "#cfc0c4" : "#c6c8cd")};
  background: ${({ $checked }) => ($checked ? "#cfc0c4" : "#fff")};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12mm;
`;

const FooterTopLine = styled.div`
  height: 0.5mm;
  background: #4e2d36;
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4mm;
`;

const TagLabel = styled.div`
  width: 25mm;
  font-size: 10pt;
  font-weight: 700;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3mm;
`;

const TagChip = styled.div`
  background: #f3f5f9;
  border-radius: 1.5mm;
  padding: 1.2mm 1.8mm;
  font-size: 9pt;
`;

const SignRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20mm;
`;

const SignColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3mm;
`;

const SignTitle = styled.div`
  font-size: 10pt;
  font-weight: 700;
`;

const SignInfoWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2mm;
  font-size: 10pt;
  line-height: 1.5;
`;

const SignImageWrap = styled.div`
  width: 16mm;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SignImage = styled.img`
  max-width: 16mm;
  max-height: 16mm;
  object-fit: contain;
`;

const SignPlaceholder = styled.div`
  color: #9aa1ab;
  font-size: 10pt;
`;

const SignDivider = styled.div`
  width: 0.1rem;
  align-self: stretch;
  background: #c6c8cd;
`;

const AttachmentCommentRow = styled.div`
  display: flex;
  gap: 10mm;
`;

const AttachmentColumn = styled.div`
  flex: 1;
`;

const CommentColumn = styled.div`
  flex: 1;
`;

const FooterLabel = styled.div`
  font-size: 10pt;
  font-weight: 700;
  margin-bottom: 0.8rem;
`;

const AttachmentList = styled.ul`
  margin: 0;
  padding-left: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 10pt;
`;

const CommentText = styled.div`
  font-size: 10pt;
  line-height: 1.5;
  white-space: pre-wrap;
`;
